/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import {
  useForm,
  getFormProps,
  getFieldProps,
  getSubmitButtonProps,
  PlainTextEditor,
  useApiClient,
  useFetchImmutable,
} from '@marageti/z4-lib';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Rating, Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Controller } from 'react-hook-form';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import StepPage from '../step-page';
import { StepFormType } from '../types';
import { getCountryNames, formatArrayToString } from '../../../utils/helpers';

const schema = z.object({
  rating: z.number().min(1),
  mainReview: z.string().min(1),
});

const Step1 = ({
  tripMatch,
  onBack,
  onNext,
  defaultValues,
  activeStep,
  error: formError,
}: StepFormType) => {
  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    control,
    reset,
  } = useForm({
    defaultValues,
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  const apiClient = useApiClient();
  const countryCodes = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const { error, helperText } = getFieldProps('rating', errors);

  if (!tripMatch || !countryCodes.data) {
    return (
      <StepPage.Skeleton />
    );
  }

  const countryNames = formatArrayToString(getCountryNames(tripMatch.countries, countryCodes.data));

  return (
    <form {...getFormProps(handleSubmit(onNext))}>
      <StepPage
        subtitle={tripMatch.partnerName}
        description={(
          <>
            <Typography variant="body">
              We would love to hear about your Zicasso trip with
              {' '}
              {tripMatch.partnerName}
            </Typography>

            <Typography variant="body">
              By sharing your feedback, you not only assist fellow travelers in making enlightened choices
              but also help Zicasso and our travel partners to consistently deliver exceptional service and experiences.
            </Typography>
          </>
        )}
        activeStep={activeStep}
        steps={2}
        fields={[[
          `How would you rate your trip to ${countryNames} by ${tripMatch.partnerName}?`,
          <Controller
            control={control}
            name="rating"
            render={({ field: { onChange, value } }) => (
              <div>
                <Rating
                  name="rating"
                  onChange={(e, r) => {
                    onChange(r);
                  }}
                  value={value}
                  sx={{ mb: 0.5 }}
                />
                {error && <Typography variant="caption" color="error">{helperText}</Typography>}

              </div>
            )}
          />,
        ],
        [
          'What are your overall comments on the trip and the travel company?',
          <Controller
            control={control}
            name="mainReview"
            render={({ field }) => (
              <PlainTextEditor
                defaultValue={defaultValues.mainReview}
                editorName="mainReview"
                error={!!errors.mainReview}
                helperText={errors.mainReview?.message}
                onChange={field.onChange}
                useLists
                sx={{
                  minHeight: { desktop: '135px' },
                  maxHeight: '135px',
                  flex: 1,
                }}
              />
            )}
          />,
        ]]}
        error={formError}
        onBack={onBack}
        nextButton={(
          <LoadingButton
            type="submit"
            variant="contained"
            {...getSubmitButtonProps(isSubmitting, isValid)}
          >
            Next
          </LoadingButton>
        )}
      />
    </form>
  );
};

export default Step1;
